import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { useCreateTrack, useUpdateTrack } from '@/hooks/use-music'
import type { Tables } from '@/types/supabase'

type MusicTrack = Tables<'music_tracks'>

interface FormState {
  track_number: string
  title: string
  duration: string  // format "mm:ss" di UI, disimpan sebagai detik
  lyrics: string
}

const EMPTY_FORM: FormState = {
  track_number: '1',
  title: '',
  duration: '',
  lyrics: '',
}

function durationToSeconds(dur: string): number | null {
  if (!dur.trim()) return null
  const parts = dur.split(':')
  if (parts.length === 2) {
    const mins = parseInt(parts[0], 10)
    const secs = parseInt(parts[1], 10)
    if (!isNaN(mins) && !isNaN(secs)) return mins * 60 + secs
  }
  return null
}

function secondsToDuration(secs: number | null | undefined): string {
  if (secs == null) return ''
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

interface TrackFormModalProps {
  open: boolean
  onClose: () => void
  releaseId: string
  track?: MusicTrack
  nextTrackNumber?: number
}

export default function TrackFormModal({
  open,
  onClose,
  releaseId,
  track,
  nextTrackNumber = 1,
}: TrackFormModalProps) {
  const isEdit = !!track
  const createTrack = useCreateTrack()
  const updateTrack = useUpdateTrack()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)

  useEffect(() => {
    if (open) {
      if (track) {
        setForm({
          track_number: String(track.track_number),
          title: track.title,
          duration: secondsToDuration(track.duration_seconds),
          lyrics: track.lyrics ?? '',
        })
      } else {
        setForm({ ...EMPTY_FORM, track_number: String(nextTrackNumber) })
      }
      createTrack.reset()
      updateTrack.reset()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, track?.id])

  const isPending = createTrack.isPending || updateTrack.isPending
  const mutationError = createTrack.error ?? updateTrack.error

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const payload = {
      release_id: releaseId,
      track_number: parseInt(form.track_number, 10) || 1,
      title: form.title.trim(),
      duration_seconds: durationToSeconds(form.duration),
      lyrics: form.lyrics.trim() || null,
    }

    if (isEdit && track) {
      updateTrack.mutate({ id: track.id, ...payload }, { onSuccess: onClose })
    } else {
      createTrack.mutate(payload, { onSuccess: onClose })
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative z-10 w-full max-w-lg rounded-xl border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold">{isEdit ? 'Edit Lagu' : 'Tambah Lagu'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* Track number + title */}
          <div className="grid grid-cols-[80px_1fr] gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">No. *</label>
              <input
                type="number"
                required
                min={1}
                value={form.track_number}
                onChange={(e) => set('track_number', e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Judul Lagu *</label>
              <input
                required
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                placeholder="Nama lagu"
              />
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Durasi</label>
            <input
              value={form.duration}
              onChange={(e) => set('duration', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              placeholder="3:45 (menit:detik)"
            />
            <p className="text-xs text-muted-foreground">Format: menit:detik, contoh 3:45</p>
          </div>

          {/* Lyrics */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Lirik</label>
            <textarea
              rows={10}
              value={form.lyrics}
              onChange={(e) => set('lyrics', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none resize-y font-mono"
              placeholder="Tulis lirik di sini (opsional)..."
            />
          </div>

          {/* Error */}
          {mutationError && (
            <p className="text-sm text-destructive">
              {(mutationError as Error).message ?? 'Terjadi kesalahan. Coba lagi.'}
            </p>
          )}

          {/* Footer actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : 'Tambah Lagu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
